import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useNotify } from '@routes-plan/ui';
import { OperationType } from '../../../models/operation-type';
import { ExpenseCategory } from '../../../models/expense-category';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, FormActions, FormSection, apiError } from '../shared/AdminUi';
import { SUBTRACT_OPERATION_ID, isSubtractOnlyCategory } from './expenseCategories';

interface FormState {
  name: string;
  categoryId: string;
  operationTypeId: string;
}

const EMPTY: FormState = { name: '', categoryId: '', operationTypeId: '' };

/**
 * Crear o editar un motivo de gasto. Los gastos de ruta y de sede siempre restan: al elegir esas
 * categorías la operación queda fija en "resta".
 */
const ExpenseTypeFormPage: React.FC<{ mode: 'new' | 'edit' }> = ({ mode }) => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canCreate, canUpdate } = useResourcePermissions('/admin/expense-types');
  const allowed = mode === 'new' ? canCreate : canUpdate;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [initial, setInitial] = useState<FormState>(EMPTY);
  const [operations, setOperations] = useState<OperationType[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [saving, setSaving] = useState(false);

  setPageTitle(mode === 'new' ? 'expenseTypes.newTitle' : 'expenseTypes.editTitle');

  const subtractOnly = isSubtractOnlyCategory(form.categoryId);
  const operationOptions = subtractOnly
    ? operations.filter((op) => op.id.toString() === SUBTRACT_OPERATION_ID)
    : operations;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [{ data: operationData }, { data: categoryData }] = await Promise.all([
          axios.get('typeoperation'),
          axios.get('expense-categories'),
        ]);
        setOperations(Array.isArray(operationData?.data) ? operationData.data : []);
        setCategories(Array.isArray(categoryData?.data) ? categoryData.data : []);
        if (mode === 'edit') {
          const { data: expense } = await axios.get(`expense-types/${id}`);
          const loaded: FormState = {
            name: expense?.name ?? '',
            categoryId: expense?.category?.id != null ? String(expense.category.id) : '',
            operationTypeId: expense?.operationType?.id != null ? String(expense.operationType.id) : '',
          };
          setForm(loaded);
          setInitial(loaded);
        }
      } catch (error) {
        notify.error(t('expenseTypes.loadFormError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  const changeCategory = (categoryId: string) => {
    setForm((prev) => ({
      ...prev,
      categoryId,
      // Gastos de ruta y de sede: la operación siempre es "resta".
      operationTypeId: isSubtractOnlyCategory(categoryId)
        ? SUBTRACT_OPERATION_ID
        : isSubtractOnlyCategory(prev.categoryId)
          ? ''
          : prev.operationTypeId,
    }));
  };

  const dirty = form.name !== initial.name || form.categoryId !== initial.categoryId || form.operationTypeId !== initial.operationTypeId;

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!allowed) return;
    const found: typeof errors = {};
    if (!form.name.trim()) found.name = t('common.required');
    if (!form.categoryId) found.categoryId = t('common.required');
    if (!form.operationTypeId) found.operationTypeId = t('common.required');
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('expenseTypes.required'));
      return;
    }
    setSaving(true);
    try {
      const body = { name: form.name, categoryId: form.categoryId, operationTypeId: form.operationTypeId };
      if (mode === 'new') {
        await axios.post('/expense-types', body);
        notify.success(t('expenseTypes.created'));
      } else {
        await axios.put(`expense-types/${id}`, body);
        notify.success(t('expenseTypes.updated'));
      }
      navigate('/admin/expense-types');
    } catch (error) {
      notify.error(apiError(error, mode === 'new' ? t('expenseTypes.createError') : t('expenseTypes.updateError')));
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'new' ? t('expenseTypes.newTitle') : t('expenseTypes.editTitle');
  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={t('expenseTypes.formSubtitle')}
        breadcrumbs={[{ label: t('expenseTypes.title'), href: '#/admin/expense-types' }, { label: title }]}
        helpAction={<ScreenTourButton />}
      />
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormSection title={t('expenseTypes.sectionData')}>
          <TextField
            id="expense-type-name"
            data-tour="expense-type-name"
            label={t('expenseTypes.description')}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            inputProps={{ maxLength: 30 }}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
          />
          <TextField
            id="expense-type-category"
            data-tour="expense-type-category"
            select
            label={t('expenseTypes.category')}
            value={categories.length ? form.categoryId : ''}
            onChange={(e) => changeCategory(e.target.value)}
            error={!!errors.categoryId}
            helperText={errors.categoryId}
            required
            fullWidth
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id.toString()}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="expense-type-operation"
            data-tour="expense-type-operation"
            select
            label={t('expenseTypes.operationType')}
            value={operations.length ? form.operationTypeId : ''}
            onChange={(e) => setForm((p) => ({ ...p, operationTypeId: e.target.value }))}
            disabled={subtractOnly}
            error={!!errors.operationTypeId}
            helperText={errors.operationTypeId ?? (subtractOnly ? t('expenseTypes.subtractOnlyHint') : undefined)}
            required
            fullWidth
          >
            {(operationOptions.length > 0 ? operationOptions : operations).map((op) => (
              <MenuItem key={op.id} value={op.id.toString()}>
                {op.name}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>
        <FormActions
          cancelTo="/admin/expense-types"
          saving={saving}
          allowed={allowed}
          deniedTooltip={mode === 'new' ? t('common.noPermissionCreate') : t('common.noPermissionEdit')}
          disabled={mode === 'edit' && !dirty}
        />
      </Box>
    </AdminPage>
  );
};

export default ExpenseTypeFormPage;
